import * as Sentry from '@sentry/react';

/**
 * Architecture Rule 48. Frontend Sentry wiring.
 *
 *  - Initialized once from `src/index.js` BEFORE `<App />` renders, so even
 *    a crash on the first render is captured.
 *  - No-ops if `REACT_APP_SENTRY_DSN` is unset (typical for local dev) — so
 *    `npm start` without Sentry credentials Just Works.
 *  - Every captured event is tagged with the last `request_id` propagated by
 *    the axios response interceptor (`window.__lastRequestId`, Rule 28) so
 *    one user click correlates with backend `audit_logs` / `outbound_http_logs`
 *    / `debug.queries`.
 *  - User identity is set/cleared by `AuthContext` on login/logout — never set
 *    `email` / `username` from anywhere else (Rule 48 PII discipline).
 */

const DEFAULT_TRACES_RATE = 0.1;
const DEFAULT_REPLAYS_SESSION_RATE = 0.0;
const DEFAULT_REPLAYS_ERROR_RATE = 1.0;

let initialized = false;

export function initSentry() {
  if (initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;

  const tracesSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? `${DEFAULT_TRACES_RATE}`,
  );
  const replaysSessionSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? `${DEFAULT_REPLAYS_SESSION_RATE}`,
  );
  const replaysOnErrorSampleRate = parseFloat(
    process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? `${DEFAULT_REPLAYS_ERROR_RATE}`,
  );

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : DEFAULT_TRACES_RATE,
    replaysSessionSampleRate: Number.isFinite(replaysSessionSampleRate) ? replaysSessionSampleRate : DEFAULT_REPLAYS_SESSION_RATE,
    replaysOnErrorSampleRate: Number.isFinite(replaysOnErrorSampleRate) ? replaysOnErrorSampleRate : DEFAULT_REPLAYS_ERROR_RATE,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Strip auth tokens from breadcrumbs before they're transmitted.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.category === 'fetch' || breadcrumb?.category === 'xhr') {
        if (breadcrumb.data?.request_headers?.Authorization) {
          breadcrumb.data.request_headers.Authorization = '[Filtered]';
        }
      }
      return breadcrumb;
    },
    beforeSend(event) {
      // Correlate Sentry event with backend log line (Rule 28).
      if (typeof window !== 'undefined' && window.__lastRequestId) {
        event.tags = { ...(event.tags || {}), request_id: window.__lastRequestId };
      }
      // Defense in depth: strip Authorization header even if Sentry SDK missed it.
      const headers = event.request?.headers;
      if (headers && (headers.Authorization || headers.authorization)) {
        delete headers.Authorization;
        delete headers.authorization;
      }
      return event;
    },
  });

  initialized = true;
}

export function setSentryUser(user) {
  if (!initialized || !user) return;
  Sentry.setUser({
    id: user.id != null ? String(user.id) : undefined,
    email: user.email || undefined,
    username: user.username || user.preferred_username || undefined,
  });
}

export function clearSentryUser() {
  if (!initialized) return;
  Sentry.setUser(null);
}

/**
 * Manual capture for caught-but-still-interesting errors (e.g., an axios
 * rejection in a useEffect that you handle gracefully but still want logged).
 * Render-time crashes are handled by `<ErrorBoundary>` (Rule 36) which calls
 * Sentry.captureException itself.
 */
export function captureError(error, extra = {}) {
  if (!initialized) return;
  Sentry.captureException(error, { extra });
}
